/**
 * Hash dashboard theme — mirrors the office system's Modern theme so a
 * customer on both products sees one cohesive design language. Bright
 * slate-on-white, blue brand accent, soft shadows, generous radii.
 *
 * The exports the rest of the app already imports (rtlTheme, ltrTheme,
 * rtlCache, ltrCache, SIDEBAR_COLORS) are preserved; the values have
 * changed.
 */
import { createTheme, alpha } from '@mui/material/styles';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';

export const MODERN_COLORS = {
  primary:       '#2563EB',
  primaryDark:   '#1E40AF',
  primaryLight:  '#DBEAFE',
  primaryDeep:   '#1B2A6B',
  secondary:     '#059669',
  bg:            '#F8FAFC',
  surface:       '#FFFFFF',
  surfaceAlt:    '#FAFBFE',
  surfaceHover:  '#F1F5F9',
  border:        '#E2E8F0',
  borderLight:   '#F1F5F9',
  text:          '#0F172A',
  textSecondary: '#64748B',
  textMuted:     '#94A3B8',
  success:       '#059669',
  warning:       '#D97706',
  danger:        '#DC2626',
  info:          '#2563EB',
} as const;

export const BRAND = {
  gradient:     'linear-gradient(135deg, #1B2A6B 0%, #3557D4 100%)',
  gradientSoft: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)',
} as const;

const sharedThemeOptions = {
  typography: {
    fontFamily: '"Rubik", "Heebo", "Inter", system-ui, -apple-system, sans-serif',
    // Hierarchy comes from size (MUI's descending scale) + restrained
    // weight. Reserving 800 for nothing keeps headings crisp, not shouty.
    h1: { fontWeight: 700, letterSpacing: '-0.022em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.018em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, fontSize: '1.05rem', letterSpacing: '-0.006em' },
    subtitle1: { fontWeight: 600, fontSize: '0.95rem' },
    subtitle2: {
      fontWeight: 600, fontSize: '0.8rem',
      textTransform: 'uppercase' as const, letterSpacing: '0.04em',
    },
    body1:   { fontSize: '0.875rem',  lineHeight: 1.6 },
    body2:   { fontSize: '0.8125rem', lineHeight: 1.55 },
    caption: { fontSize: '0.75rem' },
  },
  palette: {
    primary: {
      main: MODERN_COLORS.primary,
      dark: MODERN_COLORS.primaryDark,
      light: MODERN_COLORS.primaryLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: MODERN_COLORS.secondary,
      contrastText: '#FFFFFF',
    },
    error:   { main: MODERN_COLORS.danger,  light: '#F87171', dark: '#991B1B' },
    warning: { main: MODERN_COLORS.warning, light: '#FBBF24', dark: '#92400E' },
    success: { main: MODERN_COLORS.success, light: '#34D399', dark: '#065F46' },
    info:    { main: MODERN_COLORS.info,    light: '#60A5FA', dark: '#1E40AF' },
    background: { default: MODERN_COLORS.bg, paper: MODERN_COLORS.surface },
    text: {
      primary:   MODERN_COLORS.text,
      secondary: MODERN_COLORS.textSecondary,
    },
    divider: MODERN_COLORS.border,
  },
  shape: { borderRadius: 10 },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(15,23,42,0.04)',
    '0 1px 3px 0 rgba(15,23,42,0.06), 0 1px 2px -1px rgba(15,23,42,0.04)',
    '0 4px 6px -1px rgba(15,23,42,0.06), 0 2px 4px -2px rgba(15,23,42,0.04)',
    '0 8px 12px -3px rgba(15,23,42,0.07), 0 4px 6px -4px rgba(15,23,42,0.05)',
    '0 12px 20px -5px rgba(15,23,42,0.09), 0 8px 10px -6px rgba(15,23,42,0.05)',
    '0 18px 28px -8px rgba(15,23,42,0.12), 0 8px 14px -8px rgba(15,23,42,0.08)',
    ...Array(18).fill('0 25px 50px -12px rgba(15,23,42,0.20)'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: MODERN_COLORS.bg,
          // Flat surface — depth comes from card borders + shadows, not a
          // full-page gradient (cleaner, reads more like Stripe/Linear).
          backgroundImage: 'none',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility' as const,
          // Native controls (form widgets, scrollbars) match the light UI.
          colorScheme: 'light' as const,
          // Firefox scrollbar styling (Chrome/Safari use ::-webkit below).
          scrollbarWidth: 'thin' as const,
          scrollbarColor: '#CBD5E1 transparent',
        },
        // Honor the OS "reduce motion" setting — a baseline a11y courtesy.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        // RTL helper: flip a genuinely-directional icon (nav chevron / back
        // arrow) by adding className="rtl-flip". Do NOT flip semantic icons
        // (trending, send, play) whose meaning is fixed.
        '[dir="rtl"] .rtl-flip': { transform: 'scaleX(-1)' },
        // Big-table perf: let the browser skip rendering off-screen rows
        // (content-visibility) — most of virtualization's win, no JS and no
        // structural change. Add className="cv-rows" to a scroll-contained
        // <Table>; contain-intrinsic-size keeps the scrollbar accurate.
        '.cv-rows tbody tr': {
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 41px',
        },
        // Tabular-figure utilities so money columns line up. `.num` on any
        // numeric run; add dir="ltr" on mixed currency/sign strings in RTL.
        '.num, .tabular': {
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum" 1, "cv11" 1',
        },
        // Opt-in summary/total row: heavier figures + a hairline above.
        '.total-row > td, .total-row > th': {
          fontWeight: 700,
          borderTop: `1px solid ${MODERN_COLORS.border}`,
          backgroundColor: MODERN_COLORS.surfaceAlt,
        },
        '*:focus-visible': {
          outline: `2px solid ${MODERN_COLORS.primary}66`,
          outlineOffset: 2,
          borderRadius: 6,
        },
        '.MuiButtonBase-root:focus-visible': { outline: 'none' },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#CBD5E1', borderRadius: 4,
          border: '2px solid transparent', backgroundClip: 'padding-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#94A3B8' },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '::selection': {
          backgroundColor: `${MODERN_COLORS.primary}33`,
          color: MODERN_COLORS.text,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10, fontWeight: 600, fontSize: '0.875rem',
          textTransform: 'none' as const, padding: '8px 18px',
          transition: 'all 0.18s ease',
        },
        contained: {
          // Restrained hover: a subtle darken + soft shadow instead of a
          // translate "lift" — reads calmer and more deliberate at scale.
          boxShadow: `0 1px 2px ${alpha(MODERN_COLORS.primary, 0.16)}`,
          '&:hover': {
            backgroundColor: MODERN_COLORS.primaryDark,
            boxShadow: `0 2px 8px ${alpha(MODERN_COLORS.primary, 0.26)}`,
          },
        },
        outlined: {
          borderColor: MODERN_COLORS.border, color: MODERN_COLORS.text,
          '&:hover': {
            backgroundColor: MODERN_COLORS.surfaceHover,
            borderColor: MODERN_COLORS.primary,
          },
        },
        text: {
          '&:hover': { backgroundColor: alpha(MODERN_COLORS.primary, 0.06) },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10, transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: alpha(MODERN_COLORS.primary, 0.08) },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' as const },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10, fontSize: '0.875rem',
            transition: 'all 0.15s ease',
            '& fieldset': { borderColor: MODERN_COLORS.border },
            '&:hover fieldset': { borderColor: MODERN_COLORS.primary },
            '&.Mui-focused fieldset': { borderWidth: 2 },
          },
          '& .MuiInputLabel-root': { fontSize: '0.875rem' },
        },
      },
    },
    MuiSelect: {
      styleOverrides: { root: { borderRadius: 10, fontSize: '0.875rem' } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${MODERN_COLORS.border}`,
          boxShadow: '0 1px 3px 0 rgba(15,23,42,0.04)',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.75rem', height: 26, borderRadius: 8 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const, fontWeight: 600,
          fontSize: '0.875rem', minHeight: 44,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'inherit' as const, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: MODERN_COLORS.surface,
          backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFF 100%)',
          color: MODERN_COLORS.text,
          borderBottom: `1px solid ${MODERN_COLORS.border}`,
          boxShadow: '0 1px 0 rgba(15,23,42,0.04)',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 60, '@media (min-width: 600px)': { minHeight: 64 } },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none', borderLeft: `1px solid ${MODERN_COLORS.border}`,
          boxShadow: '1px 0 4px rgba(15,23,42,0.03)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            position: 'sticky', top: 0, zIndex: 2,
            fontWeight: 700, fontSize: '0.78rem',
            color: MODERN_COLORS.textSecondary,
            textTransform: 'uppercase' as const, letterSpacing: '0.04em',
            backgroundColor: '#F8FAFC',
            borderBottom: `1px solid ${MODERN_COLORS.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderColor: MODERN_COLORS.borderLight,
          padding: '12px 16px',
          // Tabular figures: every money/number column aligns by digit.
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum" 1',
        },
        head: { lineHeight: 1.4 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even) > td': { backgroundColor: '#FCFDFE' },
          '&:hover > td': { backgroundColor: '#F1F5F9' },
          transition: 'background-color 0.14s ease',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: '0 25px 50px -12px rgba(15,23,42,0.25)' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 800, fontSize: '1.05rem' } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 12, fontSize: '0.875rem' } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: MODERN_COLORS.borderLight } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 12, boxShadow: '0 12px 28px -8px rgba(15,23,42,0.15)',
          border: `1px solid ${MODERN_COLORS.border}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12, marginTop: 4,
          border: `1px solid ${MODERN_COLORS.border}`,
          boxShadow: '0 12px 28px -8px rgba(15,23,42,0.15)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', borderRadius: 8, margin: '2px 4px',
          '&:hover': { backgroundColor: MODERN_COLORS.surfaceHover },
          '&.Mui-selected': { backgroundColor: alpha(MODERN_COLORS.primary, 0.08) },
          '&.Mui-selected:hover': { backgroundColor: alpha(MODERN_COLORS.primary, 0.12) },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem', fontWeight: 600, background: '#0F172A',
          padding: '6px 10px', borderRadius: 8,
        },
        arrow: { color: '#0F172A' },
      },
    },
    MuiPagination: {
      styleOverrides: { root: { '& .MuiPaginationItem-root': { borderRadius: 10 } } },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: MODERN_COLORS.primary },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: MODERN_COLORS.primary },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 999, height: 8 } },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F8FAFC',
            fontWeight: 700,
            color: MODERN_COLORS.textSecondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
            fontSize: '0.78rem',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum" 1',
          },
          '& .MuiDataGrid-row:hover': { backgroundColor: MODERN_COLORS.surfaceHover },
        },
      },
    },
  },
};

export const rtlTheme = createTheme({ ...sharedThemeOptions, direction: 'rtl' });
export const ltrTheme = createTheme({ ...sharedThemeOptions, direction: 'ltr' });

export const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

export const ltrCache = createCache({
  key: 'muiltr',
});

export default rtlTheme;

// Light-surface sidebar — matches the office system's nav chrome.
export const SIDEBAR_COLORS = {
  background:      MODERN_COLORS.surface,
  backgroundLight: MODERN_COLORS.surfaceAlt,
  text:            MODERN_COLORS.text,
  textSecondary:   MODERN_COLORS.textSecondary,
  activeItem:      alpha(MODERN_COLORS.primary, 0.10),
  hoverItem:       MODERN_COLORS.surfaceHover,
  activeText:      MODERN_COLORS.primary,
  border:          MODERN_COLORS.border,
} as const;
